import { Box, DialogActions, DialogTitle, FormControl, Typography } from "@mui/material";
import { DateTime } from "luxon";
import type React from "react";
import { type FC, Fragment, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDraftMode } from "../../../contexts/DraftModeContext";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { useLastNameFirst } from "../../../hooks/useNameFormat";
import { usePredictCostBatch } from "../../../hooks/usePredictCost";
import { requestApprovalTableSortingKey } from "../../../services/LocalStorageService";
import { STATUS } from "../../../theme/tokens";
import { trackEvent } from "../../../utils/analytics";
import type { StaffingIssue } from "../../../utils/autoStaffing";
import { buildCostPrediction } from "../../../utils/costPrediction";
import { formatPersonFullName } from "../../../utils/formatters";
import {
	NotificationType,
	type Nurse,
	type NurseData,
	NurseFullRoleShort,
	type NurseInShift,
	NurseRole,
	type ReplaceShiftRequest,
	type ShiftRequest,
	type ShiftTimes,
	ShiftType,
} from "../../../utils/models";
import {
	calcualteStaffingData,
	createDateRange,
	findDateIndex,
	generateShiftData,
	getDaysUntilShift,
	isNurseChargeOnDate,
} from "../../../utils/nursePanelUtils";
import { countDailyHours } from "../../../utils/statsCalculations";
import { getEmploymentTypeDisplayName, getShiftWorkHours, getUniqueLocations } from "../../../utils/utilFunctions";
import { useStaffingTargetCheck } from "../../../utils/warningDialogs/useStaffingTargetCheck";
import { isAgencyNurse, useNurseWarnings, WarningDialog } from "../../../utils/warningDialogs/WarningDialog";
import { HeaderButton } from "../../headerButton";
import { InitiateRoboCallButton } from "../../InitiateRoboCallButton/InitiateRoboCallButton";
import { LocationPicker } from "../../LocationPicker/LocationPicker";
import { NursesMatrixGrid } from "../../NursesMatrixGrid/NursesMatrixGrid";
import { useRequestUtils } from "../../RequestData/Provider/useRequestUtils";
import { EmptyState } from "../../UIToolkit/molecules/EmptyState";
import { ApprovalDialogHeader } from "../ApprovalDialogHeader";
import { NurseTableBox, StyledDialog, StyledStyledDialog } from "../DialogUtils";
import { useRequestApproval } from "../hooks/useRequestApproval";
import { useSwapData } from "./hooks/useSwapData";
import { SwapRequestTables } from "./SwapRequestTables";
import { useGetApprovalTitle } from "./useGetApprovalTitle";

type RequestApprovalDialogProps = {
	open: boolean;
	onClose: () => void;
	onSubmit: (index: number, nurse_id?: number, location?: string) => void;
	issue: StaffingIssue;
	requestDate: DateTime;
	requestShift: ShiftType;
	neededNurses?: number;
	requestId: string;
	requestShiftTimes?: { start: DateTime; end: DateTime; date: DateTime };
	request: ShiftRequest;
	contentOnly?: boolean;
	bonus?: number;
	onDenyNurse?: (nurseId: number) => void;
	onRestoreNurse?: (nurseId: number) => void;
	queuedDenyNurseIds?: number[];
};

export const RequestApprovalDialog: FC<RequestApprovalDialogProps> = ({
	open,
	onClose,
	issue,
	requestDate,
	requestShift,
	neededNurses,
	requestId,
	requestShiftTimes,
	onSubmit,
	request,
	contentOnly,
	bonus,
	onDenyNurse,
	onRestoreNurse,
	queuedDenyNurseIds,
}) => {
	const {
		hospitalData,
		prepareDataForAddNurse,
		addNurseToShift,
		removeNurseFromSpecificDate,
		chosenDepartment,
		isSnf,
		getNurseById,
		getNurseFormattedName,
		locationsMap,
		isHospitalDemo,
	} = useHospitalData();
	const lastNameFirst = useLastNameFirst();
	const { isDraftMode } = useDraftMode();
	const { showSnackbar } = useSnackbar();
	const { isPartialShift } = useRequestUtils();
	const [selectedNurseId, setSelectedNurseId] = useState<number>();
	const [selectedRoom, setSelectedRoom] = useState<string>(request.location || "");
	const { t } = useTranslation();
	const rooms = useMemo(() => getUniqueLocations(chosenDepartment.locations, false).map((loc) => loc.location), [chosenDepartment]);
	const getTargetCheck = useStaffingTargetCheck();
	const onWarningsPassed = () => executeAddNurse();
	const [warningState, checkWarnings, handleAgencyConfirm, handleOvertimeConfirm, handleDailyHoursConfirm, handleCancelWarnings, , handleTargetConfirm] =
		useNurseWarnings(onWarningsPassed, chosenDepartment.longShiftDuration);
	const { data: requestApprovalList, isFetching } = useRequestApproval({
		departmentId: chosenDepartment.department_id,
		requestId,
	});
	const queuedDenyIdSet = useMemo(() => new Set(queuedDenyNurseIds ?? []), [queuedDenyNurseIds]);
	const hasQueuedDenies = queuedDenyIdSet.size > 0;

	// Create dateRange with useMemo - using the utility function
	const dateRange = useMemo(() => createDateRange(requestDate), [requestDate]);

	// Calculate days until the shift
	const daysUntilShift = getDaysUntilShift(requestDate);

	// The grid owns the toggle (via `deselectOnReselect`): a re-click on the committed row calls this with
	// `null`, so we just mirror whatever the grid reports and stay in sync with its checkmark.
	const handleNurseSelect = (nurse: NurseData | null) => {
		setSelectedNurseId(nurse?.id);
	};

	const handleApprove = () => {
		if (!selectedNurseId) {
			// Deny-only submit: parent's onSubmit flushes the queue without an approval.
			if (hasQueuedDenies) {
				onSubmit(0);
				return;
			}
			showSnackbar("Please select a nurse to approve", "error");
			return;
		}

		// Find the nurse to add
		const nurse = hospitalData.nurses.find((n) => n.id === selectedNurseId);

		if (!nurse) {
			showSnackbar("Could not find selected nurse", "error");
			return;
		}

		const isAgency = nurse ? isAgencyNurse(nurse.employmentType) : false;
		const overtimeHours = selectedNurseInfo.nurseData?.overtimeHours || 0;
		const overtimeCost = selectedNurseInfo.nurseData?.predictedCost || "$0";
		const dailyHours = selectedNurseInfo.nurseData?.dailyHours || 0;
		const targetWarning = getTargetCheck({ locationName: selectedRoom, weekIndex, dayNo: dayIndex, shiftIndex, isActual: false });

		checkWarnings(isAgency, overtimeHours, overtimeCost, dailyHours, false, false, targetWarning);
	};

	const executeAddNurse = () => {
		if (!selectedNurseId) return;

		const nurse = hospitalData.nurses.find((n) => n.id === selectedNurseId);
		if (!nurse) return;

		if (isSwapRequest) {
			trackEvent("Approve request dialog", {
				nurse_id: selectedNurseId,
				request_id: requestId,
				is_swap: true,
			});

			onSubmit(0, selectedNurseId);
			onClose();
			return;
		}

		// Find weekIndex, dayIndex, and shiftIndex from the dateRange
		const weekIndex = hospitalData.weeks.findIndex((week) => {
			if (!week.weeklyShifts?.length || !week.weeklyShifts[0].days?.length) return false;
			const firstDayOfWeek = week.weeklyShifts[0].days[0].date;
			const sunday = dateRange[0];
			return firstDayOfWeek.toFormat("yyyy-MM-dd") === sunday.toFormat("yyyy-MM-dd");
		});

		// Find dayIndex
		const dayIndex = findDateIndex(dateRange, requestDate);

		// Get the shift index for the request shift
		const shiftIndex = requestShift === ShiftType.Day ? 0 : 1;

		const usePartialTimes = selectedNurseInfo.isPartialShift && selectedNurseInfo.partialShiftStart && selectedNurseInfo.partialShiftEnd;
		const shiftTimesToUse =
			usePartialTimes && selectedNurseInfo.partialShiftStart && selectedNurseInfo.partialShiftEnd
				? { start: selectedNurseInfo.partialShiftStart, end: selectedNurseInfo.partialShiftEnd, date: requestDate }
				: issue.shiftTimes;

		// Execute the move with proper parameters
		if (weekIndex !== -1 && dayIndex !== -1) {
			const nurseToAdd: NurseInShift | undefined = prepareDataForAddNurse(
				hospitalData,
				weekIndex,
				dayIndex,
				shiftIndex,
				selectedNurseId,
				selectedRoom || undefined,
				false,
				requestId,
				shiftTimesToUse,
				undefined,
				bonus,
			);

			// ReplaceAgency is gated on a cancel-vs-keep choice that `approveRequest` raises AFTER this
			// runs, and dismissing it must approve nothing. Both of its branches stage their own local
			// add, so staging one here would survive a dismissal — invisibly outside draft mode, but as
			// a queued approval that publishes inside it.
			if (nurseToAdd && request.request_type !== NotificationType.ReplaceAgency) {
				addNurseToShift(nurseToAdd, weekIndex, dayIndex, shiftIndex, false, isDraftMode);
			}

			const is_replacement = issue?.suggestions[0].otherNurseId && issue?.suggestions[0].otherNurseId !== issue?.suggestions[0].nurseId;

			if (is_replacement) {
				removeNurseFromSpecificDate(weekIndex, dayIndex, shiftIndex, issue?.suggestions[0].nurseId, false, issue?.suggestions[0].shiftId, isDraftMode);
			}

			const suggestionIndex = issue.suggestions.findIndex((sug) => sug.otherNurseId === nurse.id);

			onSubmit(suggestionIndex, selectedNurseId, selectedRoom || undefined);
			trackEvent("Approve request dialog", {
				nurse_id: selectedNurseId,
				request_id: requestId,
				is_replacement: is_replacement,
				is_partial_shift: selectedNurseInfo.isPartialShift,
				...(is_replacement && {
					replacement_nurse_id: issue?.suggestions[0].otherNurseId,
				}),
			});
			onClose();
		} else {
			showSnackbar("Failed to add nurse - could not find appropriate week/day", "error");
		}
	};

	// Get the week index that matches our date range
	const weekIndex = hospitalData.weeks.findIndex((week) => {
		if (!week.weeklyShifts?.length || !week.weeklyShifts[0].days?.length) return false;

		// Get the first day of the current week
		const firstDayOfWeek = week.weeklyShifts[0].days[0].date;

		// Get Sunday date from our dateRange
		const sunday = dateRange[0];

		// Check if they're the same date
		return firstDayOfWeek.toFormat("yyyy-MM-dd") === sunday.toFormat("yyyy-MM-dd");
	});

	// Get the day index for the request date
	const dayIndex = findDateIndex(dateRange, requestDate);

	// Get the shift index for the request shift
	const shiftIndex = requestShift === ShiftType.Day ? 0 : 1;

	const nursesDataFromBackend = (requestApprovalList || [])
		.map((nurseData) => {
			const currentRequestData = request.message.request_data.find((data) => data.nurse_id === nurseData.id);
			return {
				...nurseData,
				shifts: generateShiftData({
					nurse: getNurseById(nurseData.id)!,
					weekIndex,
					hospitalData,
					targetDayIndex: dayIndex,
					sourceDayIndex: dayIndex,
					chosenDepartment,
					isSnf,
					locationsMap,
				}),
				nurseType: getEmploymentTypeDisplayName(getNurseById(nurseData.id)!.employmentType),
				...(getNurseById(nurseData.id)!.nursingStartingDate && {
					nurseStartDate: getNurseById(nurseData.id)!.nursingStartingDate,
				}),
				bonus: isPartialShift(request, nurseData.id) ? 0 : (currentRequestData?.bonus ?? request.current_bonus ?? request.bonus ?? 0),
				selectable: true,
				accepted_start_time: nurseData?.accepted_start_time ?? currentRequestData?.accepted_start_time,
				accepted_end_time: nurseData?.accepted_end_time ?? currentRequestData?.accepted_end_time,
			};
		})
		.sort((a, b) => {
			if (!a.nurseStartDate && !b.nurseStartDate) return 0;
			if (!a.nurseStartDate) return 1;
			if (!b.nurseStartDate) return -1;
			return a.nurseStartDate.toMillis() - b.nurseStartDate.toMillis();
		});

	// Batch predict costs for suggested nurses
	const suggestedNurseIds = useMemo(() => {
		return (
			issue?.suggestions
				.map((suggestion) => (suggestion.otherNurseId ? suggestion.otherNurseId : suggestion.nurseId))
				.filter((id): id is number => id != null) || []
		);
	}, [issue]);
	const { start: batchStartTime, end: batchEndTime } = requestShiftTimes || {};
	const batchPredictParams = useMemo(() => {
		if (!batchStartTime || !batchEndTime || suggestedNurseIds.length === 0) return null;
		return {
			departmentId: chosenDepartment.department_id,
			nurseIds: suggestedNurseIds,
			startTime: batchStartTime.toISO() ?? "",
			endTime: batchEndTime.toISO() ?? "",
		};
	}, [chosenDepartment.department_id, suggestedNurseIds, batchStartTime, batchEndTime]);
	const { predictions: batchPredictions } = usePredictCostBatch(batchPredictParams);

	const nursesDataLocally = useMemo<NurseData[]>(() => {
		if (weekIndex === -1) {
			return [];
		}

		const suggestedNurses =
			issue?.suggestions
				.map((suggestion) => (suggestion.otherNurseId ? suggestion.otherNurseId : suggestion.nurseId))
				.map((nurseID) => hospitalData.nurses.find((n) => n.id === nurseID))
				.filter((n): n is Nurse => n !== undefined) || [];
		const { start: startTime = DateTime.now(), end: endTime = DateTime.now() } = requestShiftTimes || {};

		const result = suggestedNurses.map<NurseData | undefined>((nurse) => {
			const nurseRequestData = request?.message.request_data.find((data) => data.nurse_id === nurse.id && data.status === "pending");
			const accepted_start_time = nurseRequestData?.accepted_start_time;
			const accepted_end_time = nurseRequestData?.accepted_end_time;

			const isCharge = isNurseChargeOnDate(hospitalData, weekIndex, shiftIndex, dayIndex, nurse.id);
			const { department_id: departmentId } = chosenDepartment;
			const { weeklyShifts = [] } = hospitalData.weeks[weekIndex] || {};

			const prediction = nurse.id != null ? batchPredictions[nurse.id] : undefined;

			const shiftWorkHours = getShiftWorkHours(
				departmentId,
				endTime,
				startTime,
				chosenDepartment.breakRulesConfig || [],
				NurseRole[NurseFullRoleShort[nurse.role] as keyof typeof NurseRole],
				nurse.employmentType,
				undefined,
				undefined,
				nurse.union,
			);
			const originalDailyHours = countDailyHours({
				nurseId: nurse.id,
				departmentId,
				weeklyShifts,
				dayIndex,
				breakRulesConfig: chosenDepartment.breakRulesConfig ?? [],
				nurseUnion: nurse.union,
			});
			// Unified cost + OT through the shared builder rather than a local copy of the delta/format rules,
			// so this dialog can't drift from the grid and the mobile cards. Also picks up `est. $X` for
			// wageless nurses, which the previous inline formatter dropped.
			const cost = buildCostPrediction(prediction, { isGlobalSalary: Boolean(nurse.isGlobalSalary) });
			const overtimeHours = cost.overtimeHours ?? 0;
			const dailyHours = originalDailyHours + shiftWorkHours;
			const predictedCostDisplay = cost.costDisplay;

			// Use the employment type display name utility function
			const nurseType = getEmploymentTypeDisplayName(nurse.employmentType);

			// Find target day index for the move
			const targetDayIndex = findDateIndex(dateRange, requestDate);

			// Find source day index for the move (same as target in this case)
			const sourceDayIndex = targetDayIndex;

			const shifts = generateShiftData({
				nurse,
				weekIndex,
				hospitalData,
				sourceDayIndex,
				targetDayIndex,
				chosenDepartment,
			});

			const isAgency = isAgencyNurse(nurse.employmentType);
			const hasOvertime = overtimeHours > 0;
			shifts[targetDayIndex].hasWarning = isAgency || hasOvertime;

			let warningMessage = "";
			if (isAgency) {
				warningMessage = "Agency nurse: Higher cost";
			}
			if (hasOvertime) {
				warningMessage = warningMessage ? `${warningMessage}, Overtime: ${overtimeHours} hours` : `Overtime: ${overtimeHours} hours`;
			}

			if (accepted_start_time || accepted_end_time) {
				warningMessage = `${warningMessage ? `${warningMessage}, ` : ""} Accepted times: ${accepted_start_time?.toFormat("h:mm a")} - ${accepted_end_time ? accepted_end_time.toFormat("h:mm a") : ""}`;
			}

			if (warningMessage) {
				shifts[targetDayIndex].tooltip = shifts[targetDayIndex].tooltip ? `${`${warningMessage}, `}\n${shifts[targetDayIndex].tooltip}` : warningMessage;
			}

			return {
				id: nurse.id,
				name: formatPersonFullName(nurse, lastNameFirst),
				role: nurse.role || "RN",
				predictedCost: predictedCostDisplay,
				nurseType,
				shifts: shifts,
				charge: isCharge,
				selectable: true,
				isShiftManager: nurse.isShiftManager,
				canBeReliefCharge: nurse.canBeReliefCharge,
				overtimeHours,
				weeklyOvertimeHoursBeforeChange: prediction?.weeklyOvertimeHoursBefore,
				dailyHours,
				trainingStatus: nurse.trainingStatus,
				...(nurse.nursingStartingDate && {
					nurseStartDate: nurse.nursingStartingDate,
				}),
				accepted_start_time,
				accepted_end_time,
				scheduledDeactivationDate: nurse.scheduledDeactivationDate ?? null,
			};
		});

		const filtered = result.filter((res): res is NurseData => !!res);

		// Sort by experience (descending - oldest hires first)
		filtered.sort((a, b) => {
			// If no start date, put at the end
			if (!a.nurseStartDate && !b.nurseStartDate) return 0;
			if (!a.nurseStartDate) return 1;
			if (!b.nurseStartDate) return -1;
			return a.nurseStartDate.toMillis() - b.nurseStartDate.toMillis();
		});

		return filtered;
	}, [
		chosenDepartment,
		hospitalData,
		weekIndex,
		shiftIndex,
		dayIndex,
		issue,
		requestShiftTimes,
		request,
		batchPredictions,
		requestDate,
		dateRange,
		lastNameFirst,
	]);

	const unsortedNursesData: NurseData[] = isHospitalDemo ? nursesDataLocally : nursesDataFromBackend;

	const nursesData = useMemo(() => {
		return unsortedNursesData.map<NurseData>((value) => ({
			...value,
			name: getNurseFormattedName(value.id),
			shiftStartTime: value.shiftTimes?.start,
			shiftEndTime: value.shiftTimes?.end,
		}));
	}, [unsortedNursesData, getNurseFormattedName]);

	const selectedNurseInfo = useMemo(() => {
		if (!selectedNurseId) {
			return {
				nurseData: undefined,
				isPartialShift: false,
				partialShiftStart: undefined,
				partialShiftEnd: undefined,
			};
		}

		const nurseData = nursesData.find((n) => n.id === selectedNurseId);
		const isPartial = isPartialShift(request, selectedNurseId);

		return {
			nurseData,
			isPartialShift: isPartial,
			partialShiftStart: nurseData?.accepted_start_time,
			partialShiftEnd: nurseData?.accepted_end_time,
		};
	}, [selectedNurseId, nursesData, request, isPartialShift]);

	const isSwapRequest = request.request_type === NotificationType.Swap;
	const isReplaceRequest = request.request_type === NotificationType.ReplaceShift;
	const swapRequest = isSwapRequest ? (request as ReplaceShiftRequest) : null;
	const swapData = useSwapData(isSwapRequest, swapRequest, requestDate, requestShift);

	const replaceNurseLocation = useMemo(() => {
		const shiftIndex = request.message.shift_period;
		return hospitalData.weeks[weekIndex]?.weeklyShifts[shiftIndex]?.days[dayIndex]?.nurses.find(
			(n) => n.nurseId === (request as ReplaceShiftRequest).replacement_nurse_id,
		)?.location;
	}, [isReplaceRequest, request, hospitalData, weekIndex]);

	const staffingData = useMemo(() => {
		return calcualteStaffingData(hospitalData, weekIndex);
	}, [hospitalData, weekIndex]);

	const swapNursesData = isSwapRequest ? swapData.originNurseData : nursesData;

	useEffect(() => {
		if (!open) return;
		const locationToMatch = replaceNurseLocation;
		const matched = rooms.find((r) => r.toLowerCase() === (locationToMatch ?? "").toLowerCase()) ?? "";
		setSelectedRoom(request.location || matched);
	}, [open, rooms, replaceNurseLocation]);

	useEffect(() => {
		if (open && isSwapRequest && swapData.originNurseData.length > 0) {
			const originNurseId = swapData.originNurseData[0]?.id;
			if (originNurseId && selectedNurseId !== originNurseId) {
				setSelectedNurseId(originNurseId);
			}
		}
	}, [open, isSwapRequest, swapData.originNurseData, selectedNurseId]);

	useEffect(() => {
		if (open) trackEvent("Open Request approval Dialog", { staffingData, nursesData });
	}, [staffingData, nursesData, open]);

	// If the selected nurse just got queued for deny, drop the approve selection so we
	// can't end up approving a nurse who is also queued for deny.
	useEffect(() => {
		if (selectedNurseId && queuedDenyIdSet.has(selectedNurseId)) {
			setSelectedNurseId(undefined);
		}
	}, [selectedNurseId, queuedDenyIdSet]);

	const requestApprovalTitle = useGetApprovalTitle({ request, neededNursesAmount: neededNurses || 1 });

	const displayShiftStart =
		selectedNurseInfo.isPartialShift && selectedNurseInfo.partialShiftStart ? selectedNurseInfo.partialShiftStart : (issue.shiftTimes as ShiftTimes).start;
	const displayShiftEnd =
		selectedNurseInfo.isPartialShift && selectedNurseInfo.partialShiftEnd ? selectedNurseInfo.partialShiftEnd : (issue.shiftTimes as ShiftTimes).end;

	const content = (
		<Fragment>
			<DialogTitle component={"div"} sx={{ p: 2, pb: 0, flex: "0 0 auto" }}>
				{contentOnly ? null : (
					<ApprovalDialogHeader
						title={requestApprovalTitle}
						shift_start={displayShiftStart}
						shift_end={displayShiftEnd}
						shiftType={requestShift}
						date={hospitalData.weeks[weekIndex].weeklyShifts[shiftIndex].days[dayIndex].date}
						isPartialShift={selectedNurseInfo.isPartialShift}
					/>
				)}

				<Typography variant="body2" sx={{ color: "text.secondary", fontSize: "14px", mb: 1 }}>
					Posted: {request.created_at.toFormat("ccc, LLL d, yyyy")}
				</Typography>

				<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: contentOnly ? 0 : 2 }}>
					{contentOnly ? null : <span />}
					<Typography variant="body2" sx={{ color: STATUS.ERROR.main, fontWeight: 500, fontSize: "14px" }}>
						The shift is in {daysUntilShift} {daysUntilShift === 1 ? "day" : "days"}
					</Typography>
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 0.25,
						}}
					>
						<InitiateRoboCallButton requestId={request.id} iconSizeOverride={"small"} date={request.message.shift_start} />
					</Box>
				</Box>

				<Typography variant="h6" sx={{ fontWeight: 600, color: "primary.dark", mb: 1, fontSize: "18px" }}>
					{isSwapRequest
						? `Origin Nurse - ${swapData.originShiftDate.toFormat("ccc, LLL d")}`
						: `Accepted By (${nursesData.filter((n) => !n.isDenied && !queuedDenyIdSet.has(n.id)).length})`}
				</Typography>
			</DialogTitle>
			<StyledStyledDialog>
				{isSwapRequest ? (
					<SwapRequestTables nursesData={swapNursesData} isFetching={isFetching} swapData={swapData} contentOnly={contentOnly} />
				) : (
					<NurseTableBox>
						<NursesMatrixGrid
							nursesData={nursesData}
							dateRange={dateRange}
							highlightDateIndexes={[dayIndex]}
							onSelectNurse={handleNurseSelect}
							isAdd={true}
							filterDate={requestDate}
							localStorageTableKey={requestApprovalTableSortingKey}
							enableColumnPicker
							request={request}
							onDenyNurse={onDenyNurse}
							onRestoreNurse={onRestoreNurse}
							queuedDenyNurseIds={queuedDenyNurseIds}
							loading={isFetching}
							emptyState={<EmptyState title={t("no.nurses.available")} />}
							deselectOnReselect
						/>
					</NurseTableBox>
				)}
			</StyledStyledDialog>
			<DialogActions sx={{ p: "12px 16px", justifyContent: "space-between", flex: "0 0 auto" }}>
				{selectedNurseId && isPartialShift(request, selectedNurseId) && (
					<Box sx={{ p: 1.5, backgroundColor: STATUS.ERROR.light, borderRadius: 1, border: `1px solid ${STATUS.ERROR.main}`, flex: 1, mr: 2 }}>
						<Typography variant="body2" sx={{ color: STATUS.ERROR.main, fontWeight: 600, mb: 0.5 }}>
							⚠️ You are about to approve a partial shift
						</Typography>
					</Box>
				)}
				<Box sx={{ display: "flex", alignItems: "center" }}>
					{!isSwapRequest && rooms.length > 0 && (
						<FormControl size="medium" onClick={(e: React.MouseEvent) => e.stopPropagation()} sx={{ width: "200px" }}>
							<LocationPicker
								data-testid="request-approval-location-dropdown"
								label="Location"
								locations={chosenDepartment.locations}
								selectedLocation={selectedRoom}
								nurseId={selectedNurseId ?? null}
								currentLocation={request.location || undefined}
								weekIndex={weekIndex}
								dayNo={dayIndex}
								shiftIndex={shiftIndex}
								isActual={false}
								clearable={!isReplaceRequest}
								disabled={isReplaceRequest}
								onChange={(location, meta) => {
									setSelectedRoom(location);
									trackEvent("Location Picker Selected", {
										surface: "approve-request",
										nurse_id: selectedNurseId,
										location,
										below_par: meta.belowPar,
										recent_shifts: meta.recentShifts,
									});
								}}
							/>
						</FormControl>
					)}
				</Box>
				<Box sx={{ display: "flex", gap: 2 }}>
					<HeaderButton dataTestId="request-approval-cancel-button" onPress={onClose} state="off" text="Cancel" disabled={false} secondary={true} />
					<HeaderButton
						dataTestId="request-approval-submit-button"
						onPress={handleApprove}
						state="on"
						text="Submit"
						disabled={!selectedNurseId && !hasQueuedDenies}
					/>
				</Box>
			</DialogActions>

			<WarningDialog
				openTargetWarning={warningState.isTargetWarningOpen}
				openAgencyWarning={warningState.isAgencyWarningOpen}
				openOvertimeWarning={warningState.isOvertimeWarningOpen}
				openDailyHoursWarning={warningState.isDailyHoursWarningOpen}
				overtimeDetails={warningState.overtimeDetails}
				dailyHoursDetails={warningState.dailyHoursDetails}
				targetDetails={warningState.targetDetails}
				longShiftDuration={chosenDepartment.longShiftDuration}
				onCancelTarget={handleCancelWarnings}
				onConfirmTarget={handleTargetConfirm}
				onCancelAgency={handleCancelWarnings}
				onConfirmAgency={handleAgencyConfirm}
				onCancelOvertime={handleCancelWarnings}
				onConfirmOvertime={handleOvertimeConfirm}
				onCancelDailyHours={handleCancelWarnings}
				onConfirmDailyHours={handleDailyHoursConfirm}
			/>
		</Fragment>
	);

	return contentOnly ? (
		content
	) : (
		<StyledDialog open={open} onClose={onClose} data-testid="request-approval-dialog">
			{content}
		</StyledDialog>
	);
};
